import apiClient from '../../Services/ApiClient';
import { useEffect, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import { format } from 'date-fns';
import NoEscape from '../NoEscape';
import { useEffectAsync } from '../../helpers';
import { USER_FULL_INFO, USER_INFO } from './UserData';
import { loadIdentificationDocumentTypes } from './UserLoader';
import useTranslation from '../../Hooks/useTranslation';

export default function UserInfo({
	userId,
	name = false,
	age = false,
	gender = false,
	formattedGender = false,
	city = false,
	street = false,
	postCode = false,
	firstName = false,
	lastName = false,
	birthDate = false,
	birthNumber = false,
	fullAddress = false,
	formattedFullAddress = false,
	email = false,
	phoneNumber = false,
	formattedPhoneNumber = false,
	identificationDocumentType = false,
	identificationDocumentNumber = false,
	historyClinicNumber = false,
	nationality = false,
	fallbackEmail = false,
	customClass = null,
	badge = false,
	useSpan = false,
	hideEmpty = false,
	insuranceCompany = false,
	profession = false,
	specialization = false,
	subSpecialization = false,
	workplaceName = false,
	rootWorkplaceName = false,
	rootWorkplaceFullAddress = false,
	emergencyContactName = false,
	emergencyContactPhoneNumber = false,
	workplaceFullAddress = false,
	showLabel = false,
	listenSocket,
}) {
	const [user, setUser] = useState(null);
	const [documentTypes, setDocumentTypes] = useState({});
	const { t } = useTranslation();
	const { translation } = useAppContext();

	const loadData = async () => {
		let result;
		if (userId) {
			result = await apiClient.graphql({
				query: USER_FULL_INFO,
				variables: { userId },
			});
		}
		const tempUser = result?.data?.user || null;
		setUser(tempUser);
	};

	// @ts-ignore
	useEffectAsync(async () => {
		await loadData();
	}, []);

	// @ts-ignore
	const { socket } = useAppContext();

	useEffect(() => {
		if (listenSocket) {
			socket.on(listenSocket, loadData);
			return () => {
				socket.off(listenSocket, loadData);
			};
		}
	}, []);

	// @ts-ignore
	useEffectAsync(async () => {
		if (identificationDocumentType) {
			const documentTypes = await loadIdentificationDocumentTypes();
			setDocumentTypes(documentTypes);
		}
	}, [identificationDocumentType]);

	const [time, setTime] = useState(null);
	document.addEventListener('refetch-react-data', () => {
		setTime(Math.floor(Date.now() / 1000));
	});

	// @ts-ignore
	useEffectAsync(async () => {
		if (time) {
			await apiClient.clear('user');
		}
	}, [time]);
	var firstUpper = function (string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	};
	const userParts = [];

	if (name && user && user.profile?.fullName) {
		userParts.push(user.profile.fullName);
	}
	if ((firstName || lastName) && user && user.profile?.fullName) {
		const fullNameParts = [];
		if (firstName) {
			fullNameParts.push(user.profile.firstName);
		}
		if (lastName) {
			fullNameParts.push(user.profile.lastName);
		}
		if (fullNameParts.length) {
			userParts.push(fullNameParts.join(' '));
		}
	}
	if (fallbackEmail && user && user.email && userParts.length === 0) {
		userParts.push(user.email);
	}
	if (email && user && user.email) {
		userParts.push(user.email);
	}
	if (phoneNumber && user && user.phoneNumber) {
		userParts.push(user.phoneNumber);
	}
	if (formattedPhoneNumber && user && user.formattedPhoneNumber) {
		userParts.push(user.formattedPhoneNumber);
	}
	if (age && user && user?.profile?.age) {
		if (showLabel) {
			userParts.push(
				firstUpper(t('base.age')) +
					': ' +
					user.profile.age +
					' ' +
					t('base.years.many'),
			);
		} else {
			userParts.push(user.profile.age + ' ' + t('base.years.many'));
		}
	}
	if (gender && user && user?.profile?.gender) {
		userParts.push(user.profile.gender);
	}
	if (formattedGender && user && user?.profile?.formattedGender) {
		userParts.push(user.profile.formattedGender);
	}
	if (birthNumber && user && user?.profile?.birthNumber) {
		userParts.push(user.profile.birthNumber);
	}
	if (badge && user && user?.badge?.title) {
		userParts.push(user.badge.title);
	}
	if (birthDate && user && user?.profile?.birthDate) {
		userParts.push(
			format(
				new Date(user.profile.birthDate),
				translation['base.dateFormatJsReact'] || 'dd.MM.yyyy',
			),
		);
	}
	if (street && user && user.profile?.address?.street) {
		const streetNumberParts = [];
		if (user.profile.address.descriptiveNumber) {
			streetNumberParts.push(user.profile.address.descriptiveNumber);
		}
		if (user.profile.address.guideNumber) {
			streetNumberParts.push(user.profile.address.guideNumber);
		}
		const streetNumber = streetNumberParts.join('/');
		userParts.push(
			user.profile.address.street + (streetNumber ? ' ' + streetNumber : ''),
		);
	}
	if (city && user && user.profile?.address?.city) {
		userParts.push(user.profile.address.city);
	}
	if (fullAddress && user && user.profile?.address?.fullAddress) {
		userParts.push(user.profile.address.fullAddress);
	}
	if (
		formattedFullAddress &&
		user &&
		user.profile?.address?.formattedFullAddress
	) {
		userParts.push(user.profile.address.formattedFullAddress);
	}
	if (postCode && user && user.profile?.address?.postCode) {
		userParts.push(user.profile.address.postCode);
	}

	const metaItems = {};
	if (user && user.meta) {
		for (const metaItem of user.meta) {
			metaItems[metaItem.key] = metaItem.value;
		}
	}

	if (insuranceCompany && user && user.profile?.insuranceCompanyTermItem) {
		userParts.push(
			user.profile.insuranceCompanyTermItem.value +
				' - ' +
				user.profile.insuranceCompanyTermItem.value2,
		);
	} else if (
		insuranceCompany &&
		metaItems &&
		metaItems['insurance'] &&
		metaItems['insurance']['companyName']
	) {
		userParts.push(metaItems['insurance']['companyName']);
	}

	if (
		identificationDocumentType &&
		metaItems &&
		metaItems['identificationDocument'] &&
		metaItems['identificationDocument']['type']
	) {
		if (
			typeof documentTypes[metaItems['identificationDocument']['type']] !==
			'undefined'
		) {
			userParts.push(
				documentTypes[metaItems['identificationDocument']['type']],
			);
		}
	}

	if (
		identificationDocumentNumber &&
		metaItems &&
		metaItems['identificationDocument'] &&
		metaItems['identificationDocument']['number']
	) {
		userParts.push(metaItems['identificationDocument']['number']);
	}

	if (historyClinicNumber && metaItems && metaItems['historyClinicNumber']) {
		if (showLabel) {
			userParts.push(
				t('diabetes.patientsInfo.historyClinicNumberShort') +
					': ' +
					metaItems['historyClinicNumber'],
			);
		} else {
			userParts.push(metaItems['historyClinicNumber']);
		}
	}

	if (nationality && user?.profile?.nationalityTermItem) {
		if (user.profile.nationalityTermItem.value === '-1') {
			userParts.push(user.profile.nationalityOther || t('base.other'));
		} else {
			userParts.push(user.profile.nationalityOther || user.profile.nationalityTermItem.name);
		}
	}

	if (profession && metaItems && metaItems['profession']) {
		userParts.push(
			metaItems['profession']['text'] || metaItems['profession']['value'],
		);
	}

	if (specialization && metaItems && metaItems['specialization']) {
		userParts.push(
			metaItems['specialization']['text'] ||
				metaItems['specialization']['value'],
		);
	}

	if (subSpecialization && metaItems && metaItems['subSpecialization']) {
		userParts.push(
			metaItems['subSpecialization']['text'] ||
				metaItems['subSpecialization']['value'],
		);
	}

	if (rootWorkplaceName && user?.rootWorkplace?.name) {
		userParts.push(user.rootWorkplace.name);
	}

	if (rootWorkplaceFullAddress && user?.rootWorkplace?.address?.fullAddress) {
		userParts.push(user.rootWorkplace.address.fullAddress);
	}

	if (workplaceFullAddress && user?.workplace?.address?.fullAddress) {
		userParts.push(user.workplace.address.fullAddress);
	}

	if (workplaceName && user?.workplace?.name) {
		userParts.push(user.workplace.name);
	}

	if (
		emergencyContactName &&
		metaItems &&
		metaItems['emergencyContact'] &&
		metaItems['emergencyContact']['name']
	) {
		userParts.push(metaItems['emergencyContact']['name']);
	}
	if (
		emergencyContactPhoneNumber &&
		metaItems &&
		metaItems['emergencyContact'] &&
		metaItems['emergencyContact']['phoneNumber']
	) {
		userParts.push(metaItems['emergencyContact']['phoneNumber']);
	}

	const complete = userParts.length || user ? userParts.join(', ') : '.....';

	if (hideEmpty && (!complete || complete === '.....')) {
		return <></>;
	}

	if (useSpan) {
		return (
			<span className={customClass}>
				<NoEscape children={complete} />
			</span>
		);
	}

	return customClass ? (
		<div className={customClass}>
			<NoEscape children={complete} />
		</div>
	) : formattedFullAddress ? (
		<NoEscape children={complete} />
	) : (
		complete
	);
}
